import { ReactElement, useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  createStyles,
  makeStyles,
  Chip,
  Theme,
  Tooltip
} from '@material-ui/core'
import { TrendingUp, TrendingDown, HelpOutline } from '@material-ui/icons'
import { YearsTaxesState } from 'ustaxes/redux'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    refundChip: {
      fontWeight: 700,
      fontSize: '0.85rem',
      padding: '4px 8px',
      height: 32
    },
    refundPositive: {
      backgroundColor: 'rgba(46, 125, 50, 0.1)',
      color: '#2E7D32',
      border: '1px solid rgba(46, 125, 50, 0.3)',
      '& .MuiChip-icon': {
        color: '#2E7D32'
      }
    },
    refundNegative: {
      backgroundColor: 'rgba(183, 28, 28, 0.1)',
      color: '#B71C1C',
      border: '1px solid rgba(183, 28, 28, 0.3)',
      '& .MuiChip-icon': {
        color: '#B71C1C'
      }
    },
    refundPending: {
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255,255,255,0.08)'
          : 'rgba(26, 35, 126, 0.06)',
      color: theme.palette.text.secondary,
      border: `1px solid ${
        theme.palette.type === 'dark'
          ? 'rgba(255,255,255,0.15)'
          : 'rgba(26, 35, 126, 0.15)'
      }`
    }
  })
)

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Math.abs(amount))
}

const RefundTracker = (): ReactElement => {
  const classes = useStyles()

  const info = useSelector(
    (state: YearsTaxesState) => state[state.activeYear]
  )

  const estimate = useMemo(() => {
    try {
      const w2s = info.w2s
      const totalWithholding = w2s.reduce(
        (sum, w2) => sum + (w2.fedWithholding || 0),
        0
      )

      const totalIncome = w2s.reduce(
        (sum, w2) => sum + (w2.income || 0),
        0
      )

      if (totalIncome === 0 && totalWithholding === 0) {
        return null
      }

      const roughTax = totalIncome * 0.22
      const roughRefund = totalWithholding - roughTax

      return {
        amount: roughRefund,
        isEstimate: true
      }
    } catch {
      return null
    }
  }, [info.w2s])

  if (!estimate) {
    return (
      <Tooltip title="Enter income data to see your estimated refund">
        <Chip
          size="small"
          icon={<HelpOutline style={{ fontSize: 16 }} />}
          label="Est. Refund: --"
          className={`${classes.refundChip} ${classes.refundPending}`}
          variant="outlined"
        />
      </Tooltip>
    )
  }

  const isRefund = estimate.amount >= 0
  const label = isRefund
    ? `Est. Refund: ${formatCurrency(estimate.amount)}`
    : `Est. Owed: ${formatCurrency(estimate.amount)}`

  return (
    <Tooltip title="Estimated based on current data. Complete all sections for accuracy.">
      <Chip
        size="small"
        icon={
          isRefund ? (
            <TrendingUp style={{ fontSize: 16 }} />
          ) : (
            <TrendingDown style={{ fontSize: 16 }} />
          )
        }
        label={label}
        className={`${classes.refundChip} ${
          isRefund ? classes.refundPositive : classes.refundNegative
        }`}
      />
    </Tooltip>
  )
}

export default RefundTracker
